import React, { Suspense, useMemo, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Center, Html, Line } from '@react-three/drei';
import { EdgesGeometry, LineSegments, LineBasicMaterial, Vector3, MathUtils } from 'three';
import html2canvas from 'html2canvas';
import './bodyvisual.css';
import { organsConfig, calculateLabelPosition } from './organsConfig';
import { getApiEndpoint } from '../../../config/oncorisk-config';


// --- No changes needed in other functions ---

function HumanBody({ dim, isLightTheme }) {
  const gltf = useGLTF(getApiEndpoint('humanBody'));
  const materialRef = useRef();


  useMemo(() => {
    if (!gltf || !gltf.scene || gltf.scene.userData.wireframe) return;
    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        if (child.material) {
          const mat = child.material.clone();
          mat.transparent = true;
          mat.opacity = 0;
          mat.depthWrite = false;
          child.material = mat;
        }
        const edges = new EdgesGeometry(child.geometry, 1.5);
        const material = new LineBasicMaterial({
          color: '#00ccff',
          linewidth: 1,
          transparent: true,
          opacity: 0,
        });
        const line = new LineSegments(edges, material);
        line.renderOrder = 1;
        child.add(line);
      }
    });
    gltf.scene.userData.wireframe = true;
  }, [gltf]);

  useEffect(() => {
    gltf.scene.traverse((child) => {
      if (child.isLineSegments) {
        materialRef.current = child.material;
      }
    });
    if (materialRef.current) {
      materialRef.current.opacity = 0;
    }
  }, [gltf.scene]);

  useFrame((state, delta) => {
    if (materialRef.current) {
      const targetOpacity = dim ? 0.2 : 0.45;
      materialRef.current.opacity = MathUtils.lerp(
        materialRef.current.opacity,
        targetOpacity,
        delta * 2.5
      );
    }
  });

  return <primitive object={gltf.scene} scale={1.5} position={[0, 1.2, 0]} />;
}

function EntryAnimation() {
  const { camera } = useThree();
  useEffect(() => {
    camera.position.set(2, 3, 4.2);
  }, [camera]);
  return null;
}

function BodyPositioner({ selectedOrgan, children }) {
  const groupRef = useRef();
  const [targetPosition, setTargetPosition] = useState([0, 0, 0]);

  useEffect(() => {
    if (!selectedOrgan) {
      setTargetPosition([0, 0, 0]);
      return;
    }
    const organ = organsConfig.find(o => o.id === selectedOrgan);
    if (!organ) return;
    const organWorldY = organ.position[1] - 9;
    setTargetPosition([
      -organ.position[0],
      2.5 - organWorldY - 5,
      -organ.position[2] + 1
    ]);
  }, [selectedOrgan]);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.lerp(new Vector3(...targetPosition), 0.1);
    }
  });

  return <group ref={groupRef}>{children}</group>;
}

function OrganMeshes({ active, diseases, gene, alteration, isLightTheme }) {
  return (
    <group>
      {organsConfig.map((organ) => {
        const isActive = active === organ.id;
        const labelPosition = calculateLabelPosition(organ);
        const organDiseases = diseases[organ.id] || [];

        return (
          <group key={organ.id}>
            <mesh position={organ.position} scale={organ.scale} rotation={organ.rotation}>
              {organ.geometry}
              <meshPhysicalMaterial
                roughness={0.1}
                transmission={1}
                thickness={0.5}
                color={isActive ? (isLightTheme ? '#2563eb' : '#00bfff') : (isLightTheme ? '#94a3b8' : '#ffffff')}
                transparent
                opacity={isActive ? 0.7 : 0.25}
              />
            </mesh>
            {/* Kidneys now use custom bean geometry in organsConfig; no extra lobes here */}
            {organ.extra && React.cloneElement(organ.extra, {
              children: React.Children.map(organ.extra.props.children, (child) => {
                // Direct material under extra
                if (child?.type === 'meshPhysicalMaterial') {
                  return React.cloneElement(child, {
                    color: isActive ? (isLightTheme ? '#2563eb' : '#00bfff') : (isLightTheme ? '#94a3b8' : '#ffffff'),
                    opacity: isActive ? 0.7 : 0.25
                  });
                }
                // If child is a mesh, recolor any material inside it
                if (child?.type === 'mesh' || child?.type?.displayName === 'mesh') {
                  return React.cloneElement(child, {
                    children: React.Children.map(child.props.children, (grand) => {
                      if (grand?.type === 'meshPhysicalMaterial') {
                        return React.cloneElement(grand, {
                          color: isActive ? (isLightTheme ? '#2563eb' : '#00bfff') : (isLightTheme ? '#94a3b8' : '#ffffff'),
                          opacity: isActive ? 0.7 : 0.25
                        });
                      }
                      return grand;
                    })
                  });
                }
                // If child is a group, recolor materials one level deeper
                if (child?.type === 'group' || child?.type?.displayName === 'group') {
                  return React.cloneElement(child, {
                    children: React.Children.map(child.props.children, (sub) => {
                      if (sub?.type === 'mesh') {
                        return React.cloneElement(sub, {
                          children: React.Children.map(sub.props.children, (grand) => {
                            if (grand?.type === 'meshPhysicalMaterial') {
                              return React.cloneElement(grand, {
                                color: isActive ? (isLightTheme ? '#2563eb' : '#00bfff') : (isLightTheme ? '#94a3b8' : '#ffffff'),
                                opacity: isActive ? 0.7 : 0.25
                              });
                            }
                            return grand;
                          })
                        });
                      }
                      return sub;
                    })
                  });
                }
                return child;
              })
            })}
            {isActive && (
              <>
                <Html distanceFactor={7} position={labelPosition.toArray()}>
                  <div className="ONCRP-TOOLS-QP-label">
                    <div className="ONCRP-TOOLS-QP-gene-variant-row">
                      <span className="ONCRP-TOOLS-QP-gene">{gene || '—'}</span>
                      <span className="ONCRP-TOOLS-QP-variant">{alteration || ''}</span>
                    </div>
                    <div className="ONCRP-TOOLS-QP-organ-count-row">
                      <span className="ONCRP-TOOLS-QP-organ">{organ.label}</span>
                      <span className="ONCRP-TOOLS-QP-disease-count">{organDiseases.length}</span>
                    </div>
                    <div className="ONCRP-TOOLS-QP-separator"></div>
                    <div className="ONCRP-TOOLS-QP-diseases-list">
                      {organDiseases.map((disease, index) => (
                        <div key={index} className="ONCRP-TOOLS-QP-disease-item">{disease}</div>
                      ))}
                    </div>
                  </div>
                </Html>
                <Line
                  points={[organ.position, [labelPosition.x, labelPosition.y + -0.2, labelPosition.z]]}
                  color={isLightTheme ? "rgba(37, 99, 235, 0.8)" : "rgba(0, 191, 255, 0.8)"}
                  lineWidth={1.5}
                />
                <mesh position={[labelPosition.x, labelPosition.y + -0.2, labelPosition.z]}>
                  <sphereGeometry args={[0.02, 8, 8]} />
                  <meshBasicMaterial color={isLightTheme ? "#2563eb" : "#00bfff"} />
                </mesh>
              </>
            )}
          </group>
        );
      })}
    </group>
  );
}

// Build diseases-per-organ map from search results
function computeOrganDiseaseMap(searchResults) {
  const map = { heart: [], brain: [], lungs: [], liver: [], kidneys: [], stomach: [], bowel: [], Other: [] };
  const decisions = [];
  const unmatched = [];
  if (!searchResults) return { map, decisions, unmatched };
  const ds = searchResults.decision_support || {};
  const associated = Array.isArray(ds.associated_diseases) ? ds.associated_diseases : [];
  const tree = searchResults.disease_tree || {};

  const tissueToOrgan = {
    lung: 'lungs', pulmonary: 'lungs', thoracic: 'lungs',
    brain: 'brain', cns: 'brain', glioma: 'brain', 'cns/brain': 'brain',
    liver: 'liver', hepatobiliary: 'liver', biliary: 'liver', cholangio: 'liver',
    kidney: 'kidneys', renal: 'kidneys',
    stomach: 'stomach', gastric: 'stomach', esophagus: 'stomach', esophageal: 'stomach',
    colon: 'bowel', colorectal: 'bowel', bowel: 'bowel', intestine: 'bowel', small: 'bowel',
    heart: 'heart', cardiac: 'heart'
  };

  const keywordToOrgan = (name = '') => {
    const n = String(name).toLowerCase();
    for (const [key, organ] of Object.entries(tissueToOrgan)) {
      if (n.includes(key)) return organ;
    }
    return 'Other';
  };

  const assignDisease = (dName, tissue) => {
    const organ = tissue ? (tissueToOrgan[String(tissue).toLowerCase()] || 'Other') : keywordToOrgan(dName);
    if (!map[organ]) map[organ] = [];
    map[organ].push(dName);
    decisions.push({ disease: dName, tissue: tissue || null, organ, method: tissue ? 'oncotree_tissue' : 'keyword' });
  };

  // Prefer disease_tree mapping with OncoTree tissue
  Object.keys(tree).forEach((dName) => {
    const t = tree[dName]?.mapping_result?.matched_oncotree_node?.tissue;
    if (t) assignDisease(dName, t);
  });

  // Fill remaining diseases by keyword heuristic
  associated.forEach((d) => {
    if (!d || !d.name) return;
    // Skip if already assigned via tree
    const already = Object.values(map).some(list => list.includes(d.name));
    if (!already) {
      const before = decisions.length;
      const organGuess = keywordToOrgan(d.name);
      assignDisease(d.name);
      if (decisions.length === before) {
        // Shouldn't happen, but keep guard
        unmatched.push(d.name);
      } else if (organGuess === 'Other') {
        unmatched.push(d.name);
      }
    }
  });

  // Detailed logs
  try {
    const counts = Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v.length]));
    // eslint-disable-next-line no-console
    console.groupCollapsed('[OncoRisk] Disease → Organ mapping');
    // eslint-disable-next-line no-console
    console.table(decisions);
    // eslint-disable-next-line no-console
    console.info('Counts per organ:', counts);
    if (unmatched.length) {
      // eslint-disable-next-line no-console
      console.warn('Unmatched/Other diseases:', unmatched);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  } catch (e) { /* noop */ }

  return { map, decisions, unmatched };
}

// =========== CORRECTED COMPONENT START ===========
function BodyVisual({ searchResults }) {
  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const canvasContainerRef = useRef(null);

  const handleSnapshot = async () => {
    if (!canvasContainerRef.current) return;

    try {
      const canvas = await html2canvas(canvasContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 2,
        logging: false,
        ignoreElements: (element) => element.classList.contains('ONCRP-TOOLS-QP-snapshot-btn')
      });

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `body_visual_${new Date().toISOString().slice(0,10)}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Failed to capture canvas:', e);
    }
  };
  // Theme detection effect
  useEffect(() => {
    const checkTheme = () => {
      if (typeof document !== 'undefined') {
        const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
        setIsLightTheme(hasLightClass);
      }
    };

    checkTheme();

    if (typeof window !== 'undefined') {
      const handleThemeChange = (event) => {
        if (event.detail && typeof event.detail.theme === 'string') {
          setIsLightTheme(event.detail.theme === 'light');
        }
      };

      window.addEventListener('ocrp-theme-change', handleThemeChange);
      return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
    }
  }, []);

  const { map: diseases } = useMemo(() => computeOrganDiseaseMap(searchResults), [searchResults]);
  const gene = searchResults?.decision_support?.variant_identity?.gene || searchResults?.after_enrichment?.custom_annotations?.OncoKB?.summary?.gene || '';
  const alteration = searchResults?.decision_support?.variant_identity?.alteration || searchResults?.after_enrichment?.custom_annotations?.OncoKB?.summary?.alteration || '';
  const [overlayInfo, setOverlayInfo] = useState({ disease: null, organ: null, tissue: null, method: null });

  useEffect(() => {
    const timer = setTimeout(() => {
      // choose organ with most diseases by default
      const counts = organsConfig.map(o => ({ id: o.id, count: (diseases[o.id] || []).length }));
      counts.sort((a,b)=> b.count - a.count);
      const defaultId = counts[0]?.id || organsConfig[0].id;
      const idx = organsConfig.findIndex(o => o.id === defaultId);
      setSelectedOrgan(defaultId);
      setSelectedIndex(idx >= 0 ? idx : 0);
      setIsReady(true);
    }, 1500); 

    return () => clearTimeout(timer);
  }, [diseases]);

  // Listen for disease selection events to focus appropriate organ
  useEffect(() => {
    const handler = (e) => {
      const name = e?.detail?.diseaseName;
      if (!name) return;
      // use disease_tree tissue if available
      const tissue = searchResults?.disease_tree?.[name]?.mapping_result?.matched_oncotree_node?.tissue;
      const { map: organMap } = computeOrganDiseaseMap(searchResults);
      let targetOrgan = null;
      if (tissue) {
        const t = String(tissue).toLowerCase();
        const tissueToOrgan = { lung: 'lungs', pulmonary: 'lungs', thoracic: 'lungs', brain: 'brain', cns: 'brain', glioma: 'brain', liver: 'liver', hepatobiliary: 'liver', biliary: 'liver', cholangio: 'liver', kidney: 'kidneys', renal: 'kidneys', stomach: 'stomach', gastric: 'stomach', esophagus: 'stomach', esophageal: 'stomach', colon: 'bowel', colorectal: 'bowel', bowel: 'bowel', intestine: 'bowel', small: 'bowel', heart: 'heart', cardiac: 'heart' };
        targetOrgan = tissueToOrgan[t] || null;
      }
      if (!targetOrgan) {
        // fall back by finding which organ list contains the disease name
        for (const [org, list] of Object.entries(organMap)) {
          if (Array.isArray(list) && list.includes(name)) { targetOrgan = org; break; }
        }
      }
      if (targetOrgan) {
        const idx = organsConfig.findIndex(o => o.id === targetOrgan);
        if (idx !== -1) { setSelectedOrgan(targetOrgan); setSelectedIndex(idx); }
        setOverlayInfo({ disease: name, organ: targetOrgan, tissue: tissue || null, method: tissue ? 'oncotree_tissue' : 'keyword' });
      }
    };
    window.addEventListener('oncrp-highlight-disease', handler);
    return () => window.removeEventListener('oncrp-highlight-disease', handler);
  }, [searchResults]);

  useEffect(() => {
    if (selectedOrgan) {
      const index = organsConfig.findIndex(o => o.id === selectedOrgan);
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [selectedOrgan]);

  const navigateOrgan = (direction) => {
    let newIndex = selectedIndex + direction;
    if (newIndex < 0) newIndex = organsConfig.length - 1;
    if (newIndex >= organsConfig.length) newIndex = 0;
    
    setSelectedIndex(newIndex);
    setSelectedOrgan(organsConfig[newIndex].id);
  };

  const handleDeselect = () => {
    setSelectedOrgan(null);
  };
  
  const handleStartExplore = () => {
    setSelectedOrgan(organsConfig[0].id);
    setSelectedIndex(0);
  };

  const currentOrgan = selectedOrgan ? organsConfig[selectedIndex] : null;
  const currentDiseases = currentOrgan ? (diseases[currentOrgan.id] || []) : [];

  return (
    <div className="ONCRP-TOOLS-QP-page" ref={canvasContainerRef}>
      <button className="ONCRP-TOOLS-QP-snapshot-btn" onClick={handleSnapshot} title="Save as PNG">
        <i className="fas fa-camera"></i>
        <span>Snapshot</span>
      </button>
      <Canvas className="ONCRP-TOOLS-QP-canvas" gl={{ preserveDrawingBuffer: true }} camera={{ fov: 35, position: [2, 3, 4.2] }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 10, 5]} intensity={1} />
        <Suspense fallback={null}>
          <BodyPositioner selectedOrgan={selectedOrgan}>
            <Center>
              <HumanBody dim={!!selectedOrgan} isLightTheme={isLightTheme} />
              <OrganMeshes active={selectedOrgan} diseases={diseases} gene={gene} alteration={alteration} isLightTheme={isLightTheme} />
            </Center>
          </BodyPositioner>
          <EntryAnimation />
        </Suspense>
        <OrbitControls enablePan={false} target={[0, 2.5, 0]} />
      </Canvas>

      {isReady && (
        <div className="ONCRP-TOOLS-QP-nav-modal">
          {selectedOrgan ? (
            <>
              <button 
                className="ONCRP-TOOLS-QP-nav-btn ONCRP-TOOLS-QP-nav-btn-left"
                onClick={() => navigateOrgan(-1)}
              >
                ←
              </button>
              
              <div className="ONCRP-TOOLS-QP-nav-info">
                <div className="ONCRP-TOOLS-QP-nav-organ-name">
                  {currentOrgan?.label}
                </div>
                <div className="ONCRP-TOOLS-QP-nav-disease-count">
                  {currentDiseases.length} diseases
                </div>
              </div>
              
              <button 
                className="ONCRP-TOOLS-QP-nav-btn ONCRP-TOOLS-QP-nav-btn-right"
                onClick={() => navigateOrgan(1)}
              >
                →
              </button>
  
              {/* THIS IS THE FIX: Changed from self-closing to a proper tag with content */}
              <button
                title="Deselect Organ"
                className="ONCRP-TOOLS-QP-nav-btn ONCRP-TOOLS-QP-deselect-btn"
                onClick={handleDeselect}
              >
                ✕
              </button>
            </>
          ) : (
            <button
              className="ONCRP-TOOLS-QP-explore-btn"
              onClick={handleStartExplore}
            >
              Explore Organs
            </button>
          )}
        </div>
      )}

      {/* Bottom-left overlay with mapping details */}
      <div className="ONCRP-TOOLS-QP-map-overlay">
        <div className="overlay-header">
          <i className="fas fa-notes-medical"></i>
          <span>Mapping</span>
        </div>
        {overlayInfo.disease ? (
          <div className="overlay-body">
            <div className="row"><span className="k">Disease</span><span className="v">{overlayInfo.disease}</span></div>
            <div className="row"><span className="k">Organ</span><span className="v">{overlayInfo.organ}</span></div>
            {overlayInfo.tissue && <div className="row"><span className="k">Tissue</span><span className="v">{overlayInfo.tissue}</span></div>}
            {overlayInfo.method && <div className="row"><span className="k">Method</span><span className="v">{overlayInfo.method}</span></div>}
          </div>
        ) : (
          <div className="overlay-body">
            <div className="row"><span className="k">Gene</span><span className="v">{gene || '—'}</span></div>
            <div className="row"><span className="k">Alteration</span><span className="v">{alteration || '—'}</span></div>
            <div className="row"><span className="k">Top Organs</span><span className="v">{
              organsConfig
                .map(o => ({ id: o.id, c: (diseases[o.id] || []).length }))
                .sort((a,b)=>b.c-a.c)
                .slice(0,2)
                .map(x => `${x.id} (${x.c})`)
                .join(', ') || '—'
            }</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
// =========== CORRECTED COMPONENT END ===========

export default BodyVisual;
